import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;
const MAX_MB = 10;
const ACCEPT = ".pdf,.doc,.docx,.txt,.md,.rtf,.odt,.zip";

const buildMessage = (questions, answers) =>
    questions
        .map((q) => `${q.label}\n${(answers[q.id] || "").trim() || "—"}`)
        .join("\n\n");

/**
 * Generic application/questionnaire page.
 *
 * Props:
 *   testid:      string  unique testid prefix (e.g. "apply-support")
 *   eyebrow:     string  small label above H1
 *   title:       JSX     H1 content with span.font-semibold accent
 *   intro:       string  intro paragraph (verbatim)
 *   packageName: string  value sent as `package` field
 *   questions:   array of { id, label, placeholder }
 *   emailLabel:  string  label of the email field (verbatim)
 */
export default function ApplyPage({
    testid,
    eyebrow,
    title,
    intro,
    packageName,
    questions,
    emailLabel,
}) {
    const fileRef = useRef(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [answers, setAnswers] = useState(
        Object.fromEntries(questions.map((q) => [q.id, ""])),
    );
    const [file, setFile] = useState(null);
    const [fileErr, setFileErr] = useState("");
    const [consent, setConsent] = useState(false);
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const onAnswer = (id, value) =>
        setAnswers((prev) => ({ ...prev, [id]: value }));

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        setFileErr("");
        if (!f) {
            setFile(null);
            return;
        }
        if (f.size > MAX_MB * 1024 * 1024) {
            setFileErr(`Файл больше ${MAX_MB} МБ. Уменьшите размер или отправьте в Telegram.`);
            setFile(null);
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        setFile(f);
    };

    const clearFile = () => {
        setFile(null);
        setFileErr("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const formatBytes = (b) => {
        if (b < 1024) return `${b} Б`;
        if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} КБ`;
        return `${(b / (1024 * 1024)).toFixed(2)} МБ`;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) {
            setErrorMsg("Заполните имя и email.");
            setStatus("error");
            return;
        }
        if (!consent) {
            setErrorMsg("Подтвердите согласие на обработку персональных данных.");
            setStatus("error");
            return;
        }
        setStatus("loading");
        setErrorMsg("");
        try {
            const fd = new FormData();
            fd.append("name", name.trim());
            fd.append("email", email.trim());
            fd.append("message", buildMessage(questions, answers));
            fd.append("package", packageName);
            if (file) fd.append("file", file);

            await axios.post(`${API}/contact`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setStatus("success");
            toast.success("Заявка отправлена.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : "Не удалось отправить. Попробуйте Telegram @ekulakova1.";
            setErrorMsg(msg);
            setStatus("error");
            toast.error(msg);
        }
    };

    return (
        <div
            data-testid={`${testid}-page`}
            className="min-h-screen bg-[#F4F1EA] text-[#1B2A41] font-mont"
        >
            <header className="border-b border-[#1B2A41]/10 bg-[#F4F1EA]/85 backdrop-blur sticky top-0 z-10">
                <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid={`${testid}-back-link`}
                        className="text-[11px] tracking-[0.4em] uppercase font-semibold text-[#1B2A41]/70 hover:text-[#C46342] transition-colors"
                    >
                        ← Внутри
                    </Link>
                    <span className="text-[11px] tracking-[0.32em] uppercase text-[#1B2A41]/45">
                        Анкета · {eyebrow.replace(/^Формат \d+ · /, "")}
                    </span>
                </div>
            </header>

            <main className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
                    <aside className="lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-12 h-px bg-[#C46342]" />
                            <span className="text-[11px] tracking-[0.4em] uppercase font-semibold text-[#C46342]">
                                {eyebrow}
                            </span>
                        </div>
                        <h1
                            data-testid={`${testid}-title`}
                            className="text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] text-[#1B2A41] font-extralight leading-[1.04] mb-10"
                        >
                            {title}
                        </h1>
                        <p className="text-base sm:text-lg leading-[1.65] text-[#1B2A41]/85 font-light max-w-md">
                            {intro}
                        </p>
                    </aside>

                    <section className="lg:col-span-7">
                        {status === "success" ? (
                            <div
                                data-testid={`${testid}-success`}
                                className="border border-[#C46342] bg-white p-10 lg:p-14"
                            >
                                <div className="text-[11px] tracking-[0.4em] uppercase font-semibold text-[#C46342] mb-6">
                                    Спасибо
                                </div>
                                <p className="text-xl sm:text-2xl leading-[1.5] text-[#1B2A41] font-light mb-8">
                                    Спасибо, я получила ваш запрос. Посмотрю
                                    ваши ответы и вернусь с предложением по
                                    формату и времени для встречи. Обычно я
                                    отвечаю в течение рабочего дня.
                                </p>
                                <p className="text-base text-[#1B2A41]/70 font-medium">— Елена</p>
                                <Link
                                    to="/"
                                    data-testid={`${testid}-success-home`}
                                    className="mt-10 inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase font-semibold text-[#C46342] hover:text-[#1B2A41] transition-colors"
                                >
                                    Вернуться на главную →
                                </Link>
                            </div>
                        ) : (
                            <form
                                data-testid={`${testid}-form`}
                                onSubmit={onSubmit}
                                encType="multipart/form-data"
                                className="border border-[#1B2A41]/20 bg-white p-8 lg:p-12"
                            >
                                <div className="space-y-8">
                                    <div>
                                        <label
                                            htmlFor={`${testid}-name`}
                                            className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                        >
                                            Как к вам обращаться? *
                                        </label>
                                        <input
                                            id={`${testid}-name`}
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            data-testid={`${testid}-input-name`}
                                            required
                                            className="w-full bg-transparent border-0 border-b border-[#1B2A41]/25 focus:border-[#C46342] outline-none py-3 text-base text-[#1B2A41] placeholder:text-[#1B2A41]/30 transition-colors"
                                            placeholder="Имя"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor={`${testid}-email`}
                                            className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                        >
                                            {emailLabel}
                                        </label>
                                        <input
                                            id={`${testid}-email`}
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            data-testid={`${testid}-input-email`}
                                            required
                                            className="w-full bg-transparent border-0 border-b border-[#1B2A41]/25 focus:border-[#C46342] outline-none py-3 text-base text-[#1B2A41] placeholder:text-[#1B2A41]/30 transition-colors"
                                            placeholder="you@company.com"
                                        />
                                    </div>

                                    {questions.map((q) => (
                                        <div key={q.id}>
                                            <label
                                                htmlFor={`${testid}-${q.id}`}
                                                className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                            >
                                                {q.label}
                                            </label>
                                            <textarea
                                                id={`${testid}-${q.id}`}
                                                rows="3"
                                                value={answers[q.id]}
                                                onChange={(e) => onAnswer(q.id, e.target.value)}
                                                data-testid={`${testid}-input-${q.id}`}
                                                className="w-full bg-transparent border border-[#1B2A41]/20 focus:border-[#C46342] outline-none py-3 px-4 text-base text-[#1B2A41] placeholder:text-[#1B2A41]/30 transition-colors resize-none"
                                                placeholder={q.placeholder}
                                            />
                                        </div>
                                    ))}

                                    <div>
                                        <label
                                            htmlFor={`${testid}-file`}
                                            className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                        >
                                            Прикрепить файл · по желанию
                                        </label>
                                        <input
                                            ref={fileRef}
                                            id={`${testid}-file`}
                                            type="file"
                                            accept={ACCEPT}
                                            onChange={onFileChange}
                                            data-testid={`${testid}-input-file`}
                                            className="hidden"
                                        />
                                        {!file ? (
                                            <label
                                                htmlFor={`${testid}-file`}
                                                data-testid={`${testid}-file-dropzone`}
                                                className="block cursor-pointer border border-dashed border-[#1B2A41]/35 p-5 text-center hover:border-[#C46342] hover:bg-[#F4F1EA] transition-colors"
                                            >
                                                <div className="flex items-center justify-center gap-3 text-sm text-[#1B2A41]/75">
                                                    <span className="inline-flex w-8 h-8 items-center justify-center bg-[#1B2A41] text-[#F4F1EA]">
                                                        +
                                                    </span>
                                                    <span>
                                                        <span className="font-semibold text-[#1B2A41]">
                                                            Прикрепить файл
                                                        </span>{" "}
                                                        · до {MAX_MB} МБ
                                                    </span>
                                                </div>
                                                <div className="mt-2 text-[11px] tracking-[0.18em] uppercase text-[#1B2A41]/50">
                                                    PDF · DOC · DOCX · TXT · MD · RTF · ODT · ZIP
                                                </div>
                                            </label>
                                        ) : (
                                            <div
                                                data-testid={`${testid}-file-preview`}
                                                className="flex items-center justify-between gap-4 border border-[#1B2A41]/20 bg-[#F4F1EA] p-4"
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <span className="inline-flex w-8 h-8 shrink-0 items-center justify-center bg-[#C46342] text-[#F4F1EA] text-sm font-semibold">
                                                        ✓
                                                    </span>
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-semibold text-[#1B2A41] truncate">
                                                            {file.name}
                                                        </div>
                                                        <div className="text-[11px] tracking-[0.18em] uppercase text-[#1B2A41]/60">
                                                            {formatBytes(file.size)}
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={clearFile}
                                                    data-testid={`${testid}-file-clear`}
                                                    className="shrink-0 text-[11px] tracking-[0.18em] uppercase font-semibold text-[#1B2A41]/60 hover:text-[#C46342] transition-colors"
                                                >
                                                    Убрать
                                                </button>
                                            </div>
                                        )}
                                        {fileErr && (
                                            <div
                                                data-testid={`${testid}-file-error`}
                                                className="mt-2 text-xs text-[#C46342]"
                                            >
                                                {fileErr}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {status === "error" && errorMsg && (
                                    <div
                                        data-testid={`${testid}-error`}
                                        className="mt-8 px-4 py-3 border border-[#C46342] bg-[#F4F1EA] text-sm text-[#1B2A41]"
                                    >
                                        {errorMsg}
                                    </div>
                                )}

                                <div className="mt-10 border-t border-[#1B2A41]/10 pt-6">
                                    <label
                                        htmlFor={`${testid}-consent`}
                                        data-testid={`${testid}-consent-label`}
                                        className="flex items-start gap-3 cursor-pointer group select-none"
                                    >
                                        <input
                                            id={`${testid}-consent`}
                                            type="checkbox"
                                            checked={consent}
                                            onChange={(e) => setConsent(e.target.checked)}
                                            data-testid={`${testid}-consent-checkbox`}
                                            required
                                            className="mt-[3px] shrink-0 h-4 w-4 appearance-none border border-[#1B2A41]/40 bg-white checked:bg-[#C46342] checked:border-[#C46342] focus:outline-none focus:ring-2 focus:ring-[#C46342]/30 cursor-pointer transition-colors"
                                            style={{
                                                backgroundImage: consent
                                                    ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23F4F1EA' stroke-width='2' stroke-linecap='square' d='M3 8l3.5 3.5L13 5'/%3E%3C/svg%3E\")"
                                                    : "none",
                                                backgroundSize: "100% 100%",
                                                backgroundRepeat: "no-repeat",
                                            }}
                                        />
                                        <span className="text-sm leading-relaxed text-[#1B2A41]/85">
                                            Отправляя заявку, вы соглашаетесь на
                                            обработку персональных данных в
                                            соответствии с{" "}
                                            <Link
                                                to="/privacy"
                                                data-testid={`${testid}-consent-privacy-link`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-[#C46342] font-medium underline underline-offset-4 decoration-[#C46342]/40 hover:decoration-[#C46342] transition-colors"
                                            >
                                                Политикой&nbsp;конфиденциальности
                                            </Link>
                                            .
                                        </span>
                                    </label>
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <button
                                        type="submit"
                                        data-testid={`${testid}-submit`}
                                        disabled={status === "loading"}
                                        className="inline-flex items-center justify-center gap-3 bg-[#C46342] hover:bg-[#9d4d33] disabled:opacity-60 disabled:cursor-not-allowed text-[#F4F1EA] px-8 py-4 text-xs font-semibold tracking-[0.18em] uppercase transition-colors"
                                    >
                                        {status === "loading" ? "Отправляю…" : "Отправить анкету"}
                                        {status !== "loading" && (
                                            <svg
                                                width="16"
                                                height="12"
                                                viewBox="0 0 18 12"
                                                fill="none"
                                            >
                                                <path
                                                    d="M1 6H17M17 6L12 1M17 6L12 11"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                </div>
            </main>
        </div>
    );
}
